'use client';

import { useEffect, useState } from 'react';
import { Wallet, ArrowDown, ArrowUp, RefreshCw, Loader2, ChevronLeft, ChevronRight, Plus, Banknote, DollarSign, TrendingUp, ExternalLink } from 'lucide-react';
import { useWallet } from '@/hooks/useWallet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';

export function WalletPage() {
  const { balance, transactions, withdrawals, payoutMethods, loading, fetchBalance, fetchTransactions, fetchWithdrawals, fetchPayoutMethods, requestWithdrawal, addPayoutMethod, deletePayoutMethod, setDefaultPayoutMethod } = useWallet();
  const [activeTab, setActiveTab] = useState<'overview' | 'transactions' | 'withdrawals' | 'payout-methods'>('overview');
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);
  const [payoutMethodDialogOpen, setPayoutMethodDialogOpen] = useState(false);
  const [selectedPayoutMethod, setSelectedPayoutMethod] = useState<string>('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [newPayoutMethod, setNewPayoutMethod] = useState<{ type: 'bank_transfer' | 'crypto' | 'paypal' | 'wise' | 'stripe'; name: string; details: Record<string, string> }>({ type: 'bank_transfer', name: '', details: {} as Record<string, string> });
  const [addingMethod, setAddingMethod] = useState(false);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [withdrawalsPage, setWithdrawalsPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchBalance();
    fetchTransactions({ page: transactionsPage, limit: itemsPerPage });
    fetchWithdrawals({ page: withdrawalsPage, limit: itemsPerPage });
    fetchPayoutMethods();
  }, [fetchBalance, fetchTransactions, fetchWithdrawals, fetchPayoutMethods, transactionsPage, withdrawalsPage]);

  const handleWithdraw = async () => {
    if (!withdrawAmount || !selectedPayoutMethod || !balance) return;
    const payoutMethod = payoutMethods.find(pm => pm.id === selectedPayoutMethod);
    const address = payoutMethod?.details?.address || payoutMethod?.details?.email || payoutMethod?.details?.accountNumber || '';
    setWithdrawing(true);
    const success = await requestWithdrawal({ amount: parseFloat(withdrawAmount), payoutMethodId: selectedPayoutMethod, currency: balance.currency, address });
    if (success) {
      setWithdrawAmount('');
      setSelectedPayoutMethod('');
      setWithdrawDialogOpen(false);
    }
    setWithdrawing(false);
  };

  const handleAddPayoutMethod = async () => {
    if (!newPayoutMethod.name || Object.keys(newPayoutMethod.details).length === 0) return;
    setAddingMethod(true);
    const success = await addPayoutMethod({ type: newPayoutMethod.type, details: newPayoutMethod.details, label: newPayoutMethod.name });
    if (success) {
      setNewPayoutMethod({ type: 'bank_transfer', name: '', details: {} });
      setPayoutMethodDialogOpen(false);
    }
    setAddingMethod(false);
  };

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'reward': return <ArrowUp className="h-4 w-4 text-green-400" />;
      case 'withdrawal': return <ArrowDown className="h-4 w-4 text-red-400" />;
      case 'bonus': return <Banknote className="h-4 w-4 text-purple-400" />;
      case 'referral': return <DollarSign className="h-4 w-4 text-amber-400" />;
      case 'refund': return <RefreshCw className="h-4 w-4 text-blue-400" />;
      default: return <Wallet className="h-4 w-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
        return <Badge className="bg-green-500/10 text-green-400">{status}</Badge>;
      case 'pending':
      case 'processing':
        return <Badge className="bg-amber-500/10 text-amber-400">{status}</Badge>;
      case 'rejected':
      case 'failed':
      case 'cancelled':
        return <Badge className="bg-red-500/10 text-red-400">{status}</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const payoutMethodTypes = [
    { value: 'bank_transfer', label: 'Bank Transfer', fields: ['accountNumber', 'routingNumber', 'accountName', 'bankName'] },
    { value: 'crypto', label: 'Cryptocurrency', fields: ['address', 'network', 'walletType'] },
    { value: 'paypal', label: 'PayPal', fields: ['email'] },
    { value: 'wise', label: 'Wise', fields: ['email', 'accountId'] },
    { value: 'stripe', label: 'Stripe', fields: ['accountId'] },
  ];

  const selectedPayoutType = payoutMethodTypes.find(t => t.value === newPayoutMethod.type);

  if (loading && !balance) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="glass">
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-1/4 mb-2" />
                <Skeleton className="h-8 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Wallet</h1>
          <p className="text-slate-400 mt-1">Manage your earnings and withdrawals</p>
        </div>
        <Button variant="outline">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {balance && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Withdrawable</p>
                  <p className="text-2xl font-bold text-green-400 mt-1">{formatCurrency(balance.buckets.withdrawable)}</p>
                </div>
                <div className="p-3 bg-green-500/10 rounded-xl">
                  <Wallet className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Pending</p>
                  <p className="text-2xl font-bold text-amber-400 mt-1">{formatCurrency(balance.buckets.pending)}</p>
                </div>
                <div className="p-3 bg-amber-500/10 rounded-xl">
                  <ArrowUp className="h-6 w-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Approved</p>
                  <p className="text-2xl font-bold text-blue-400 mt-1">{formatCurrency(balance.buckets.approved)}</p>
                </div>
                <div className="p-3 bg-blue-500/10 rounded-xl">
                  <TrendingUp className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="glass">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Balance</p>
                  <p className="text-2xl font-bold text-white mt-1">{formatCurrency(balance.totalBalance)}</p>
                </div>
                <div className="p-3 bg-purple-500/10 rounded-xl">
                  <DollarSign className="h-6 w-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={(v: any) => setActiveTab(v)} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 bg-slate-800/50">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="withdrawals">Withdrawals</TabsTrigger>
          <TabsTrigger value="payout-methods">Payout Methods</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="glass">
              <CardHeader>
                <CardTitle>Balance Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {balance && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/10 rounded-lg">
                          <ArrowUp className="h-4 w-4 text-green-400" />
                        </div>
                        <span className="text-slate-300">Rewards (Approved)</span>
                      </div>
                      <span className="font-bold text-green-400">{formatCurrency(balance.buckets.approved)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-500/10 rounded-lg">
                          <ArrowUp className="h-4 w-4 text-amber-400" />
                        </div>
                        <span className="text-slate-300">Rewards (Pending)</span>
                      </div>
                      <span className="font-bold text-amber-400">{formatCurrency(balance.buckets.pending)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-500/10 rounded-lg">
                          <Banknote className="h-4 w-4 text-purple-400" />
                        </div>
                        <span className="text-slate-300">Bonuses</span>
                      </div>
                      <span className="font-bold text-purple-400">{formatCurrency(balance.buckets.bonus)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-500/10 rounded-lg">
                          <DollarSign className="h-4 w-4 text-amber-400" />
                        </div>
                        <span className="text-slate-300">Referrals</span>
                      </div>
                      <span className="font-bold text-amber-400">{formatCurrency(balance.buckets.referral)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-500/10 rounded-lg">
                          <Wallet className="h-4 w-4 text-red-400" />
                        </div>
                        <span className="text-slate-300">Frozen</span>
                      </div>
                      <span className="font-bold text-red-400">{formatCurrency(balance.buckets.frozen)}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-800/50 rounded-xl border border-green-500/20">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/20 rounded-lg">
                          <Wallet className="h-4 w-4 text-green-400" />
                        </div>
                        <span className="text-slate-300 font-medium">Withdrawable</span>
                      </div>
                      <span className="font-bold text-green-400 text-lg">{formatCurrency(balance.buckets.withdrawable)}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="glass">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full justify-start gap-3" variant="outline">
                        <ArrowDown className="h-5 w-5 text-green-400" />
                        <span>Withdraw Funds</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Withdraw Funds</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-300">Amount (USD)</label>
                          <Input
                            type="number"
                            step="0.01"
                            min="1"
                            max={balance?.buckets.withdrawable || 0}
                            value={withdrawAmount}
                            onChange={e => setWithdrawAmount(e.target.value)}
                            placeholder="0.00"
                          />
                          <p className="text-xs text-slate-500">Available: {formatCurrency(balance?.buckets.withdrawable || 0)}</p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-300">Payout Method</label>
                          <Select value={selectedPayoutMethod} onValueChange={(v) => setSelectedPayoutMethod(v)}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select payout method" />
                            </SelectTrigger>
                            <SelectContent>
                              {payoutMethods.filter(m => m.verified).map(method => (
                                <SelectItem key={method.id} value={method.id}>
                                  {method.name} ({method.type}) {method.isDefault && '★'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleWithdraw} disabled={withdrawing || !withdrawAmount || !selectedPayoutMethod}>
                          {withdrawing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Withdraw'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={payoutMethodDialogOpen} onOpenChange={setPayoutMethodDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full justify-start gap-3" variant="outline">
                        <Plus className="h-5 w-5 text-cyan-400" />
                        <span>Add Payout Method</span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Add Payout Method</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-300">Type</label>
<Select value={newPayoutMethod.type} onValueChange={(v: 'bank_transfer' | 'crypto' | 'paypal' | 'wise' | 'stripe') => setNewPayoutMethod(prev => ({ ...prev, type: v }))}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {payoutMethodTypes.map(t => (
                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-300">Name</label>
                          <Input
                            placeholder="My Bank Account"
                            value={newPayoutMethod.name}
                            onChange={e => setNewPayoutMethod(prev => ({ ...prev, name: e.target.value }))}
                          />
                        </div>
                        {selectedPayoutType && (
                          <div className="space-y-2">
                            {selectedPayoutType.fields.map(field => (
                              <div key={field} className="space-y-2">
                                <label className="text-sm font-medium text-slate-300">{field.replace(/([A-Z])/g, ' $1')}</label>
                                <Input
                                  placeholder={field}
                                  value={newPayoutMethod.details[field] || ''}
                                  onChange={e => setNewPayoutMethod(prev => ({
                                    ...prev,
                                    details: { ...prev.details, [field]: e.target.value }
                                  }))}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setPayoutMethodDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleAddPayoutMethod} disabled={addingMethod}>
                          {addingMethod ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Add Method'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="transactions">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Bucket</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length > 0 ? (
                    transactions.map(tx => (
                      <TableRow key={tx.id}>
                        <TableCell>{formatDateTime(tx.createdAt)}</TableCell>
                        <TableCell className="flex items-center gap-2">
                          {getTransactionTypeIcon(tx.type)}
                          <span className="capitalize">{tx.type}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">{tx.bucket}</Badge>
                        </TableCell>
                        <TableCell className={cn('font-bold', tx.amount >= 0 ? 'text-green-400' : 'text-red-400')}>
                          {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                        </TableCell>
                        <TableCell>{getStatusBadge(tx.status)}</TableCell>
                        <TableCell className="text-slate-400 max-w-xs truncate">{tx.description}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">No transactions yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between mt-4">
                <Button variant="outline" size="sm" onClick={() => setTransactionsPage(p => Math.max(1, p - 1))} disabled={transactionsPage === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-slate-300">Page {transactionsPage}</span>
                <Button variant="outline" size="sm" onClick={() => setTransactionsPage(p => p + 1)} disabled={transactions.length < itemsPerPage}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="withdrawals">
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Withdrawal History</CardTitle>
                <Dialog open={withdrawDialogOpen} onOpenChange={setWithdrawDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <ArrowDown className="mr-2 h-4 w-4" />
                      Withdraw
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Withdraw Funds</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-300">Amount (USD)</label>
                        <Input type="number" step="0.01" min="1" max={balance?.buckets.withdrawable || 0} value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} placeholder="0.00" />
                        <p className="text-xs text-slate-500">Available: {formatCurrency(balance?.buckets.withdrawable || 0)}</p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-300">Payout Method</label>
                        <Select value={selectedPayoutMethod} onValueChange={(v) => setSelectedPayoutMethod(v)}>
                          <SelectTrigger><SelectValue placeholder="Select payout method" /></SelectTrigger>
                          <SelectContent>
                            {payoutMethods.filter(m => m.verified).map(method => (
                              <SelectItem key={method.id} value={method.id}>{method.name} ({method.type}) {method.isDefault && '★'}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setWithdrawDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleWithdraw} disabled={withdrawing || !withdrawAmount || !selectedPayoutMethod}>{withdrawing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Withdraw'}</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Net Amount</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.length > 0 ? (
                    withdrawals.map(w => (
                      <TableRow key={w.id}>
                        <TableCell>{formatDateTime(w.createdAt)}</TableCell>
                        <TableCell>{formatCurrency(w.amount)}</TableCell>
                        <TableCell className="text-red-400">-{formatCurrency(w.feeAmount)}</TableCell>
                        <TableCell className="font-bold text-green-400">{formatCurrency(w.netAmount)}</TableCell>
                        <TableCell>{payoutMethods.find(m => m.id === w.payoutMethodId)?.name || 'Unknown'}</TableCell>
                        <TableCell>{getStatusBadge(w.status)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-400">No withdrawals yet</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between mt-4">
                <Button variant="outline" size="sm" onClick={() => setWithdrawalsPage(p => Math.max(1, p - 1))} disabled={withdrawalsPage === 1}><ChevronLeft className="h-4 w-4" /></Button>
                <span className="text-sm text-slate-300">Page {withdrawalsPage}</span>
                <Button variant="outline" size="sm" onClick={() => setWithdrawalsPage(p => p + 1)} disabled={withdrawals.length < itemsPerPage}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payout-methods">
          <Card className="glass">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Payout Methods</CardTitle>
                <Dialog open={payoutMethodDialogOpen} onOpenChange={setPayoutMethodDialogOpen}>
                  <DialogTrigger asChild>
                    <Button><Plus className="mr-2 h-4 w-4" />Add Method</Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Add Payout Method</DialogTitle></DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2"><label className="text-sm font-medium text-slate-300">Type</label>
                        <Select value={newPayoutMethod.type} onValueChange={(v: 'bank_transfer' | 'crypto' | 'paypal' | 'wise' | 'stripe') => setNewPayoutMethod(prev => ({ ...prev, type: v }))}>
                          <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                          <SelectContent>{payoutMethodTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2"><label className="text-sm font-medium text-slate-300">Name</label>
                        <Input placeholder="My Bank Account" value={newPayoutMethod.name} onChange={e => setNewPayoutMethod(prev => ({ ...prev, name: e.target.value }))} />
                      </div>
                      {selectedPayoutType && (
                        <div className="space-y-2">{selectedPayoutType.fields.map(field => (
                          <div key={field} className="space-y-2">
                            <label className="text-sm font-medium text-slate-300">{field.replace(/([A-Z])/g, ' $1')}</label>
                            <Input placeholder={field} value={newPayoutMethod.details[field] || ''} onChange={e => setNewPayoutMethod(prev => ({ ...prev, details: { ...prev.details, [field]: e.target.value } }))} />
                          </div>
                        ))}</div>
                      )}
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setPayoutMethodDialogOpen(false)}>Cancel</Button><Button onClick={handleAddPayoutMethod} disabled={addingMethod}>{addingMethod ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Add Method'}</Button></DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {payoutMethods.length > 0 ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  {payoutMethods.map(method => (
                    <div key={method.id} className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-cyan-500/10 rounded-lg">
                            <ExternalLink className="h-5 w-5 text-cyan-400" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{method.name}</p>
                            <p className="text-sm text-slate-400 capitalize">{method.type.replace('_', ' ')}</p>
                          </div>
                        </div>
                        {method.isDefault && <Badge className="bg-green-500/10 text-green-400">Default</Badge>}
                      </div>
                      <div className="space-y-2 text-sm text-slate-400">
                        {Object.entries(method.details).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span>{key.replace(/([A-Z])/g, ' $1')}:</span>
                            <span className="font-mono">{value}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-700/50">
                        {!method.isDefault && (
                          <Button size="sm" variant="outline" className="flex-1" onClick={() => setDefaultPayoutMethod(method.id)}>Set Default</Button>
                        )}
                        <Button size="sm" variant="destructive" onClick={() => deletePayoutMethod(method.id)}>Delete</Button>
                      </div>
                      {method.verified && <Badge className="mt-2 bg-green-500/10 text-green-400">Verified</Badge>}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ExternalLink className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                  <p className="text-slate-400">No payout methods added</p>
                  <Button className="mt-4" onClick={() => setPayoutMethodDialogOpen(true)}><Plus className="mr-2 h-4 w-4" />Add First Method</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}